import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { Pagination } from "@/components/Pagination";
import { fmtCurrencyPrecise } from "@/lib/format";
import { ArrowRight, Plus, CopySimple, CheckCircle, MagnifyingGlass, X } from "@phosphor-icons/react";

const MSTATUS = [["open", "statusOpen"], ["paid", "statusPaid"], ["complete", "statusComplete"], ["cancel", "statusCancel"]];
const MSTATUS_CLS = { open: "text-blue-600", paid: "text-emerald-600", complete: "text-violet-600", cancel: "text-red-600" };
const TH = "p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground";

export default function OrdersPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => axios.get(`${API}/admin/orders`).then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [perPage, search, dateFrom, dateTo, statusFilter]);

  const orderDate = (o) => (o.created_at ? String(o.created_at).slice(0, 10) : "");
  const filtered = orders.filter((o) => {
    const s = search.trim().toLowerCase();
    if (s) {
      const hay = `${o.invoice_number || ""} ${o.business_name || ""} ${o.coop_code || ""}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    const d = orderDate(o);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    if (statusFilter !== "all" && (o.manual_status || "open") !== statusFilter) return false;
    return true;
  });
  const hasFilters = search || dateFrom || dateTo || statusFilter !== "all";
  const clearFilters = () => { setSearch(""); setDateFrom(""); setDateTo(""); setStatusFilter("all"); };

  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const patch = (id, upd) => setOrders((os) => os.map((o) => (o.id === id ? { ...o, ...upd } : o)));

  const changeStatus = async (id, manual_status) => {
    patch(id, { manual_status });
    try { await axios.put(`${API}/admin/orders/${id}`, { manual_status }); }
    catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); load(); }
  };
  const duplicate = async (id) => {
    setBusy(id);
    try { const { data } = await axios.post(`${API}/admin/orders/${id}/duplicate`); navigate(`/admin/orders/${data.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="px-4 sm:px-6 py-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.backOffice}</div>
            <h1 className="mt-1 font-display text-4xl font-black tracking-tighter text-foreground">{t.ordersManagement}</h1>
          </div>
          <button data-testid="new-order-button" onClick={() => navigate("/admin/orders/new")}
            className="inline-flex items-center gap-2 bg-primary px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5">
            <Plus size={16} weight="bold" /> {t.newOrder}
          </button>
        </div>
        <div className="mb-4 flex flex-wrap items-end gap-3 border border-border bg-card p-4">
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.searchOrders}</label>
            <div className="flex items-center border border-border bg-background px-3 focus-within:border-primary">
              <MagnifyingGlass size={16} weight="bold" className="text-muted-foreground" />
              <input data-testid="orders-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t.searchOrdersHint} className="w-full bg-transparent px-2 py-2 text-sm outline-none" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.dateFrom}</label>
            <input data-testid="orders-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.dateTo}</label>
            <input data-testid="orders-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.status}</label>
            <select data-testid="orders-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm font-bold outline-none focus:border-primary">
              <option value="all">{t.allStatuses}</option>
              {MSTATUS.map(([v, k]) => <option key={v} value={v}>{t[k]}</option>)}
            </select>
          </div>
          {hasFilters && (
            <button data-testid="orders-clear-filters" onClick={clearFilters} className="inline-flex items-center gap-1 border border-border px-3 py-2 text-xs font-bold text-muted-foreground hover:bg-muted transition-colors duration-200">
              <X size={14} weight="bold" /> {t.clearFilters}
            </button>
          )}
        </div>
        <div className="mb-4 flex items-center justify-between gap-4">
          <div data-testid="orders-result-count" className="text-xs text-muted-foreground">{filtered.length} {filtered.length === 1 ? t.resultSingular : t.resultPlural}</div>
          <Pagination testid="orders-pagination-top" page={page} perPage={perPage} total={filtered.length} onPage={setPage} onPerPage={setPerPage} />
        </div>
        <div className="border border-border bg-card overflow-x-auto">
          <table data-testid="orders-table" className="w-full min-w-[1000px] text-sm">
            <thead>
              <tr className="border-b-2 border-border text-left">
                <th className={TH}>{t.invoiceNumber}</th>
                <th className={TH}>{t.orderFrom}</th>
                <th className={TH}>{t.coopClientCode}</th>
                <th className={TH}>{t.placedOn}</th>
                <th className={TH}>{t.status}</th>
                <th className={TH}>COOP</th>
                <th className={`${TH} text-right`}>{t.grandTotal}</th>
                <th className={`${TH} text-right`}>{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">{t.loading}</td></tr>}
              {!loading && orders.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">{t.noOrders}</td></tr>}
              {!loading && orders.length > 0 && filtered.length === 0 && <tr><td colSpan={8} data-testid="orders-no-results" className="p-6 text-center text-muted-foreground">{t.noOrdersMatch}</td></tr>}
              {!loading && paged.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-border hover:bg-muted/50 transition-colors duration-200">
                  <td className="p-3 font-mono text-xs font-bold text-foreground cursor-pointer" onClick={() => navigate(`/admin/orders/${o.id}`)} data-testid={`invoice-cell-${o.id}`}>{o.invoice_number || "—"}</td>
                  <td className="p-3 font-medium text-foreground cursor-pointer" onClick={() => navigate(`/admin/orders/${o.id}`)}>{o.business_name}</td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">{o.coop_code || "—"}</td>
                  <td className="p-3 text-xs text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA") : "—"}</td>
                  <td className="p-3">
                    <select data-testid={`status-select-${o.id}`} value={o.manual_status || "open"} onChange={(e) => changeStatus(o.id, e.target.value)}
                      className={`border border-border bg-background px-2 py-1 text-xs font-bold outline-none focus:border-primary ${MSTATUS_CLS[o.manual_status || "open"]}`}>
                      {MSTATUS.map(([v, k]) => <option key={v} value={v}>{t[k]}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    {o.coop_sent
                      ? <span data-testid={`coop-sent-${o.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-teal-600"><CheckCircle size={14} weight="fill" /> {t.coopSent}</span>
                      : <span data-testid={`coop-none-${o.id}`} className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="p-3 text-right font-semibold tabular-nums text-foreground">{o.grand_total != null ? fmtCurrencyPrecise(o.grand_total, lang) : "—"}</td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`duplicate-${o.id}`} onClick={() => duplicate(o.id)} disabled={busy === o.id} title={t.duplicate}
                        className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs font-bold text-foreground hover:bg-muted transition-colors duration-200 disabled:opacity-50"><CopySimple size={14} weight="bold" /></button>
                      <button data-testid={`open-${o.id}`} onClick={() => navigate(`/admin/orders/${o.id}`)} className="text-muted-foreground hover:text-primary"><ArrowRight size={16} weight="bold" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex justify-end">
          <Pagination testid="orders-pagination-bottom" page={page} perPage={perPage} total={filtered.length} onPage={setPage} onPerPage={setPerPage} />
        </div>
      </main>
    </div>
  );
}
